import {
  KeyRound,
  Power,
  Search,
  ShieldCheck,
  UserCog,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROTULO_PAPEL,
  PAPEIS,
  type Papel,
  type UsuarioDoEscritorio,
} from '@social/shared';
import { AcoesLinha } from '../componentes/acoes-linha';
import { AlternadorVisao, type Visao } from '../componentes/alternador-visao';
import { CartaoKpi } from '../componentes/cartao-kpi';
import { PaginaShell } from '../componentes/pagina-shell';
import { SeletorOpcao, type OpcaoSeletor } from '../componentes/seletor-opcao';
import { Tabela, type Coluna } from '../componentes/tabela';
import { Aviso, Botao, CONTROLE_BARRA, Entrada } from '../componentes/ui';
import { ErroApi } from '../lib/api';
import { api, useSessao } from '../lib/sessao';
import { DialogoNovoUsuario, DialogoPapel, DialogoSenha } from './usuarios-dialogos';

/*
  A EQUIPE do escritório — só para administrador.

  Nasce do MESMO molde da consulta de pessoas: barra de uma linha, KPI enxuto,
  `Tabela` com cards abaixo de `sm`, ações na linha. Nada aqui é decisão nova
  de layout — se parecer diferente das outras consultas, é defeito.

  ## As seis perguntas (skill `social-desenho`)
  1. **Qual pergunta a pessoa traz?** "Quem tem acesso ao meu escritório, com
     qual papel — e como ponho, tiro ou destravo alguém."
  2. **Quantos registros na vida real?** Unidades a poucas dezenas: um
     escritório de contabilidade tem de 3 a 15 pessoas. Por isso a lista vem
     inteira numa requisição e o filtro é em memória.
  3. **Resultado vs. aparência?** Busca e papel mudam o resultado e ficam
     soltos na barra; lista/cards é aparência.
  4. **Barra vs. gaveta?** Sem gaveta: com dois filtros ela ficaria vazia, e
     gaveta vazia é clique que não devolve nada.
  5. **375px?** A `Tabela` troca para cards e as ações colapsam no menu.
  6. **O que promete e não existe?** Não há tela de detalhe nem exclusão de
     usuário. Desligar é o que existe, e é o que a ação diz.

  ## Por que as três ações são DIÁLOGO
  Criar, trocar papel e definir senha são escolhas: pedem a atenção inteira, e
  o que está atrás não ajuda a decidir. Ver o cabeçalho de `usuarios-dialogos`.
*/

const OPCOES_PAPEL: readonly OpcaoSeletor<Papel | ''>[] = [
  { valor: '', rotulo: 'Todos os papéis', Icone: Users },
  ...PAPEIS.map((p) => ({ valor: p, rotulo: ROTULO_PAPEL[p], Icone: UserCog })),
];

export function Usuarios(): JSX.Element {
  const eu = useSessao((s) => s.eu);
  const cache = useQueryClient();

  const [busca, setBusca] = useState('');
  const [papel, setPapel] = useState<Papel | ''>('');
  const [visao, setVisao] = useState<Visao>('lista');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [criando, setCriando] = useState(false);
  const [senhaDe, setSenhaDe] = useState<UsuarioDoEscritorio | null>(null);
  const [papelDe, setPapelDe] = useState<UsuarioDoEscritorio | null>(null);

  const lista = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api.get<UsuarioDoEscritorio[]>('/usuarios'),
  });

  const recarregar = async () => {
    await cache.invalidateQueries({ queryKey: ['usuarios'] });
  };

  const aoFalhar = (e: unknown) => {
    setAviso(null);
    setErro(e instanceof ErroApi ? e.message : 'Não foi possível concluir.');
  };

  const alterar = useMutation({
    mutationFn: (v: { id: string; ativo: boolean }) =>
      api.patch<UsuarioDoEscritorio>(`/usuarios/${v.id}`, { ativo: v.ativo }),
    onSuccess: async () => {
      setErro(null);
      await recarregar();
    },
    onError: aoFalhar,
  });

  const todos = useMemo(() => lista.data ?? [], [lista.data]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return todos.filter(
      (u) =>
        (papel === '' || u.papel === papel) &&
        (termo === '' ||
          u.nome.toLowerCase().includes(termo) ||
          u.email.toLowerCase().includes(termo)),
    );
  }, [todos, busca, papel]);

  const kpis = useMemo(
    () => ({
      total: todos.length,
      ativos: todos.filter((u) => u.ativo).length,
      administradores: todos.filter((u) => u.papel === 'administrador' && u.ativo).length,
    }),
    [todos],
  );

  const haRecorte = busca !== '' || papel !== '';

  const colunas: Coluna<UsuarioDoEscritorio>[] = [
    {
      chave: 'nome',
      titulo: 'Nome',
      papel: 'titulo',
      celula: (u) => (
        <span className="text-tinta-900 font-medium">
          {u.nome}
          {u.id === eu?.id && (
            <span className="text-tinta-500 pl-2 text-xs font-normal">você</span>
          )}
        </span>
      ),
    },
    {
      chave: 'email',
      titulo: 'E-mail',
      celula: (u) => <span className="text-tinta-500 break-all">{u.email}</span>,
    },
    {
      chave: 'papel',
      titulo: 'Papel',
      celula: (u) => <span className="text-tinta-700">{ROTULO_PAPEL[u.papel]}</span>,
    },
    {
      chave: 'situacao',
      titulo: 'Situação',
      ocultarNoCelular: true,
      celula: (u) =>
        u.ativo ? (
          <span className="bg-ok-100 text-ok-600 rounded-full px-2 py-0.5 text-xs">
            Ativo
          </span>
        ) : (
          <span className="bg-tinta-100 text-tinta-700 rounded-full px-2 py-0.5 text-xs">
            Desligado
          </span>
        ),
    },
  ];

  return (
    <PaginaShell
      titulo="Equipe"
      subtitulo="Quem entra no escritório, e com qual papel"
      acoes={
        <Botao onClick={() => setCriando(true)}>
          <UserPlus className="size-4" aria-hidden />
          Novo usuário
        </Botao>
      }
      filtros={
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
          <div className="col-span-full flex items-center gap-2 lg:min-w-72 lg:flex-1">
            <div className="relative min-w-0 flex-1">
              <Search
                className="text-tinta-500 pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
                aria-hidden
              />
              {/*
                Filtra ao DIGITAR, sem botão de consultar — diferente das
                consultas de sociedades e pessoas, e de propósito: aqui a lista
                inteira já está em memória (uma requisição, dezenas de linhas),
                e confirmar a busca seria um clique para percorrer um array. Lá
                a consulta vai ao servidor, e o botão evita uma ida por tecla.
              */}
              <Entrada
                aria-label="Buscar usuário"
                placeholder="Buscar por nome ou e-mail…"
                className="pr-9 pl-9"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
              {busca && (
                <button
                  type="button"
                  onClick={() => setBusca('')}
                  aria-label="Limpar a busca"
                  title="Limpar a busca"
                  className="text-tinta-500 hover:text-tinta-900 absolute top-1/2 right-2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors"
                >
                  <X className="size-4" aria-hidden />
                </button>
              )}
            </div>
          </div>

          <div className="lg:contents [&_button]:w-full lg:[&_button]:w-auto">
            <SeletorOpcao<Papel | ''>
              rotulo="Filtrar por papel"
              IconeGatilho={UserCog}
              larguraPainel="w-72"
              valor={papel}
              aoEscolher={setPapel}
              opcoes={OPCOES_PAPEL}
            />
          </div>

          <div className="col-span-full flex items-center justify-end gap-2 lg:contents">
            {haRecorte && (
              <button
                type="button"
                onClick={() => {
                  setBusca('');
                  setPapel('');
                }}
                title="Limpar busca e papel"
                className={`${CONTROLE_BARRA} px-3`}
              >
                <X className="size-4" aria-hidden />
                Limpar
              </button>
            )}
            <AlternadorVisao visao={visao} aoTrocar={setVisao} />
          </div>
        </div>
      }
      indicadores={
        todos.length > 0 ? (
          <>
            <CartaoKpi rotulo="Usuários" valor={String(kpis.total)} icone={Users} />
            <CartaoKpi
              rotulo="Ativos"
              valor={String(kpis.ativos)}
              detalhe="ocupam vaga do plano"
              icone={Power}
              tom="ok"
            />
            <CartaoKpi
              rotulo="Admins"
              valor={String(kpis.administradores)}
              detalhe="administradores ativos"
              icone={ShieldCheck}
              tom="marca"
            />
          </>
        ) : undefined
      }
    >
      {lista.isError && <Aviso>Não foi possível carregar a equipe.</Aviso>}

      {erro && (
        <p
          role="alert"
          className="bg-erro-100 text-erro-600 rounded-lg px-4 py-3 text-sm"
        >
          {erro}
        </p>
      )}
      {aviso && (
        <p role="status" className="bg-ok-100 text-ok-600 rounded-lg px-4 py-3 text-sm">
          {aviso}
        </p>
      )}

      <Tabela
        colunas={colunas}
        visao={visao}
        acoes={(u) => (
          <AcoesLinha
            acoes={[
              {
                rotulo: `Alterar papel de ${u.nome}`,
                icone: UserCog,
                aoEscolher: () => {
                  setErro(null);
                  setPapelDe(u);
                },
              },
              {
                rotulo: `Definir senha de ${u.nome}`,
                icone: KeyRound,
                aoEscolher: () => {
                  setErro(null);
                  setSenhaDe(u);
                },
              },
              {
                rotulo: `${u.ativo ? 'Desligar' : 'Reativar'} ${u.nome}`,
                icone: Power,
                aoEscolher: () => alterar.mutate({ id: u.id, ativo: !u.ativo }),
              },
            ]}
          />
        )}
        cartao={(u, acoesDaLinha) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-tinta-900 font-medium">
                {u.nome}
                {u.id === eu?.id && (
                  <span className="text-tinta-500 pl-2 text-xs font-normal">você</span>
                )}
              </p>
              <p className="text-tinta-500 text-sm break-all">{u.email}</p>
              <p className="text-tinta-500 pt-1 text-xs">
                {ROTULO_PAPEL[u.papel]}
                {!u.ativo && ' · Desligado'}
              </p>
            </div>
            {acoesDaLinha}
          </div>
        )}
        pagina={{
          itens: filtrados,
          total: filtrados.length,
          pagina: 1,
          totalPaginas: 1,
          tamanho: filtrados.length || 1,
        }}
        aoTrocarPagina={() => undefined}
        carregando={lista.isPending}
        chaveDe={(u) => u.id}
        /*
          A lista cabe numa página: um escritório tem de 3 a 15 pessoas, e não
          há paginação a trocar. O vazio, porém, é obrigatório — e aqui ele
          tem dois textos diferentes, porque "ninguém encontrado" e "ninguém
          cadastrado" mandam a pessoa fazer coisas opostas.
        */
        vazio={
          haRecorte
            ? {
                titulo: 'Ninguém encontrado.',
                descricao: 'Nenhum usuário bate com a busca ou o papel escolhido.',
              }
            : {
                titulo: 'Só você por enquanto.',
                descricao: 'Crie o primeiro usuário para dividir o trabalho.',
              }
        }
      />

      {/*
        Dito na tela, não escondido no código: quem administra precisa saber
        por que a ação vai recusar ANTES de tentar.
      */}
      <p className="text-tinta-500 flex items-start gap-2 text-xs">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <span>
          Ninguém muda o próprio papel nem se desliga, e o último administrador ativo não
          pode ser rebaixado — é o que impede o escritório de ficar sem quem administre.
          Definir senha encerra as sessões abertas da pessoa.
        </span>
      </p>

      <DialogoNovoUsuario
        aberto={criando}
        aoFechar={() => setCriando(false)}
        aoCriar={async () => {
          setCriando(false);
          setErro(null);
          setAviso('Usuário criado.');
          await recarregar();
        }}
        aoFalhar={(e) => {
          setCriando(false);
          aoFalhar(e);
        }}
      />

      <DialogoPapel
        usuario={papelDe}
        aoFechar={() => setPapelDe(null)}
        aoAlterar={async (u, novo) => {
          setPapelDe(null);
          setErro(null);
          setAviso(`${u.nome} agora é ${ROTULO_PAPEL[novo].toLowerCase()}.`);
          await recarregar();
        }}
        aoFalhar={(e) => {
          setPapelDe(null);
          aoFalhar(e);
        }}
      />

      <DialogoSenha
        usuario={senhaDe}
        aoFechar={() => setSenhaDe(null)}
        aoDefinir={(u) => {
          setSenhaDe(null);
          setErro(null);
          setAviso(`Senha de ${u.nome} definida. As sessões dele foram encerradas.`);
        }}
        aoFalhar={(e) => {
          setSenhaDe(null);
          aoFalhar(e);
        }}
      />
    </PaginaShell>
  );
}
